import { ONBOARDED_FEED_QUEST_ID } from "@/constants";
import { notifyError } from "@/utils/monitoring/newrelic";
import type {
  BaseFieldData,
  CTAFieldData,
  DiscoverFeedItem,
  LandingFieldData,
  QuizFieldData,
  RewardFieldData,
  VideoFieldData,
} from "./Discover";
import { QuizHeaderToCMSType } from "./Discover";
import { VideoUrl } from "./VideoUrl";

type FieldDataMap = {
  video: VideoFieldData;
  quiz: QuizFieldData;
  cta: CTAFieldData;
  reward: RewardFieldData;
  landing: LandingFieldData;
  pintohome: BaseFieldData;
  dailygems: BaseFieldData;
  bonusgems: BaseFieldData;
  referral: BaseFieldData;
  allcaughtup: never;
  socialconnection: never;
  signin: never;
};

type TemplateTypes = keyof FieldDataMap;

type TemplateConvertersMap = {
  [K in keyof FieldDataMap]: (data: FieldDataMap[K]) => DiscoverFeedItem;
};

const toBaseTemplate = (fieldData: BaseFieldData) => {
  return {
    id: fieldData.slug,
    questCompletePopupText: fieldData["quest-complete-popup-text"],
    quest: {
      questId: fieldData.slug,
      gemsEarnable: fieldData["gems-earnable-2"],
    },
    bypass: fieldData.bypass,
    day0: fieldData.day0 ?? false,
    onboardingExperience: fieldData["onboarding-experience"],
    priority: fieldData.priority,
    categories: fieldData.categories
      ? fieldData.categories.split(",").map((cat) => cat.trim().toLowerCase())
      : [],
    tags: fieldData.tags
      ? fieldData.tags.split(",").map((tag) => tag.trim().toLowerCase())
      : [],
    createdAt: fieldData.createdAt,
  };
};

const toRewardTemplate = (
  fieldData: RewardFieldData,
): DiscoverFeedItem | undefined => {
  if (!fieldData["reward-background"]) {
    notifyError(
      "Reward template is missing required field: reward-background",
      "discoverFeedError",
    );
    return undefined;
  }

  return {
    ...toBaseTemplate(fieldData),
    type: "reward",
    quest: {
      questId: `${ONBOARDED_FEED_QUEST_ID}-event`,
      gemsEarnable: fieldData["gems-earnable-2"],
    },
    rewardBackground: new URL(fieldData["reward-background"]),
    cta:
      !fieldData["cta-url"] || !fieldData["cta-label"]
        ? undefined
        : {
            label: fieldData["cta-label"],
            description: fieldData["cta-description"] || "",
            url: fieldData["cta-url"],
          },
    rewardSigninRedirectUrl: fieldData["reward-signin-redirect-url"]
      ? new URL(fieldData["reward-signin-redirect-url"])
      : undefined,
  };
};

const toCTATemplate = (
  fieldData: CTAFieldData,
): DiscoverFeedItem | undefined => {
  if (!fieldData["cta-image"]) {
    notifyError(
      "CTA template is missing required field: cta-image",
      "discoverFeedError",
    );
    return undefined;
  }

  return {
    ...toBaseTemplate(fieldData),
    type: "cta",
    game: {
      id: fieldData["game-id"],
      name: fieldData["game-name"],
    },
    image: new URL(fieldData["cta-image"].url),
    title: fieldData["video-title"],
    description: fieldData["cta-description"],
    buttonLabel: fieldData["cta-label"],
    buttonURL: fieldData["cta-url"],
  };
};

const toVideoTemplate = (
  fieldData: VideoFieldData,
): DiscoverFeedItem | undefined => {
  if (!fieldData["video-url"]) {
    notifyError(
      "Video template is missing required field: video-url",
      "discoverFeedError",
    );
    return undefined;
  }

  return {
    ...toBaseTemplate(fieldData),
    game: {
      id: fieldData["game-id"],
      name: fieldData["game-name"],
    },
    type: "video",
    title: fieldData["video-title"],
    videoUrl: new VideoUrl(fieldData["video-url"]),
    cta:
      !fieldData["cta-url"] || !fieldData["cta-label"]
        ? undefined
        : {
            description: fieldData["cta-label"],
            url: fieldData["cta-url"],
          },
  };
};

const toQuizTemplate = (
  fieldData: QuizFieldData,
): DiscoverFeedItem | undefined => {
  if (
    !fieldData["quiz-question"] ||
    !fieldData["quiz-answers"] ||
    !fieldData["quiz-correct-answers"] ||
    fieldData["quiz-answers"].split(", ").length === 0 ||
    fieldData["quiz-correct-answers"].split(", ").length === 0
  ) {
    notifyError(
      "Quiz template is missing required fields",
      "discoverFeedError",
      fieldData,
    );
    return undefined;
  }

  return {
    ...toBaseTemplate(fieldData),
    game: {
      id: fieldData["game-id"],
      name: fieldData["game-name"],
    },
    type: "quiz",
    logoUrl: fieldData["quiz-logo"]
      ? new URL(fieldData["quiz-logo"].url)
      : undefined,
    header:
      fieldData["quiz-header-type"] === QuizHeaderToCMSType.Video
        ? {
            type: "video",
            url: new VideoUrl(fieldData["quiz-header-url"]),
          }
        : {
            type: "image",
            initialImage: fieldData["quiz-header-initial-image"]
              ? new URL(fieldData["quiz-header-initial-image"].url)
              : undefined,
            answeredImage: fieldData["quiz-header-answered-image"]
              ? new URL(fieldData["quiz-header-answered-image"].url)
              : undefined,
          },
    question: fieldData["quiz-question"],
    answers: fieldData["quiz-answers"].split(", ").map((a) => a.trim()),
    correctAnswers: fieldData["quiz-correct-answers"]
      ? fieldData["quiz-correct-answers"]
          .split(", ")
          .map((a) => Number.parseInt(a.trim()))
      : [],
    correctAnswerMessage: fieldData["quiz-correct-answer-message"],
  };
};

const toLandingTemplate = (
  fieldData: LandingFieldData,
): DiscoverFeedItem | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "landing",
    gameId: fieldData["game-id"],
  };
};

const toPinToHomeTemplate = (
  fieldData: BaseFieldData,
): DiscoverFeedItem | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "pintohome",
  };
};

const toDailyGemsTemplate = (
  fieldData: BaseFieldData,
): DiscoverFeedItem | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "dailygems",
  };
};

const toBonusGemsTemplate = (
  fieldData: BaseFieldData,
): DiscoverFeedItem | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "bonusgems",
  };
};

const toReferralTemplate = (
  fieldData: BaseFieldData,
): DiscoverFeedItem | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "referral",
  };
};

const templateConverters: TemplateConvertersMap = {
  video: toVideoTemplate,
  quiz: toQuizTemplate,
  cta: toCTATemplate,
  reward: toRewardTemplate,
  landing: toLandingTemplate,
  pintohome: toPinToHomeTemplate,
  dailygems: toDailyGemsTemplate,
  bonusgems: toBonusGemsTemplate,
  referral: toReferralTemplate,
  // These templates are created programmatically and not from the CMS
  allcaughtup: () => undefined,
  socialconnection: () => undefined,
  signin: () => undefined,
};

export function convertFieldDataToTemplate<T extends TemplateTypes>(
  questTypeName: T,
  fieldData: FieldDataMap[T],
): DiscoverFeedItem {
  try {
    const converter = templateConverters[questTypeName];
    if (!converter) {
      notifyError(
        `No converter found for quest type: ${questTypeName}`,
        "discoverFeedError",
        questTypeName,
      );
      return undefined;
    }

    // Type assertion is safe here because we've already validated the quest type
    return converter(fieldData);
  } catch (error) {
    notifyError(
      error instanceof Error ? error.message : new Error(String(error)),
      "discoverFeedError",
      error,
    );
    return undefined;
  }
}
